import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const RATE_PER_BIN = 80;
const BAGS_PER_BIN = 35;
const DAILY_TARGET_BINS = 450;

const initialWorkers = [
  { id: 1, name: "Sipho Dlamini", role: "Harvester", phone: "071 234 5678", startDate: "2024-01-15" },
  { id: 2, name: "Thandi Nkosi", role: "Harvester", phone: "082 345 6789", startDate: "2024-01-15" },
  { id: 3, name: "Lucky Mokoena", role: "Senior Harvester", phone: "063 456 7890", startDate: "2023-11-01" },
  { id: 4, name: "Zanele Khumalo", role: "Harvester", phone: "071 567 8901", startDate: "2024-02-01" },
  { id: 5, name: "Bongani Mthembu", role: "Team Leader", phone: "082 678 9012", startDate: "2023-08-15" },
];

const todayStr = new Date().toISOString().split("T")[0];

function generateDailyData(workers) {
  const data = {};
  workers.forEach(w => {
    data[w.id] = {
      signIn: null, signOut: null, bags: 0, hoursWorked: 0,
    };
  });
  return data;
}

export default function CitrusFarmSystem() {
  const [tab, setTab] = useState("dashboard");
  const [workers, setWorkers] = useState(initialWorkers);
  const [dailyRecords, setDailyRecords] = useState({ [todayStr]: generateDailyData(initialWorkers) });
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [showAddWorker, setShowAddWorker] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [newWorker, setNewWorker] = useState({ name: "", role: "Harvester", phone: "", startDate: todayStr });
  const [bagsInput, setBagsInput] = useState({});
  const [toast, setToast] = useState(null);
  const [monthFilter, setMonthFilter] = useState(new Date().toISOString().slice(0, 7));

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const getRecord = (date = selectedDate) => dailyRecords[date] || {};

  const ensureDateRecord = (date) => {
    if (!dailyRecords[date]) {
      setDailyRecords(prev => ({
        ...prev,
        [date]: generateDailyData(workers)
      }));
    }
  };

  const signIn = (workerId) => {
    const now = new Date();
    const time = now.toTimeString().slice(0, 5);
    setDailyRecords(prev => ({
      ...prev,
      [selectedDate]: {
        ...prev[selectedDate],
        [workerId]: { ...prev[selectedDate]?.[workerId], signIn: time, signOut: null }
      }
    }));
    showToast(`${workers.find(w => w.id === workerId)?.name} signed in at ${time}`);
  };

  const signOut = (workerId) => {
    const rec = getRecord()[workerId];
    if (!rec?.signIn) return;
    const now = new Date();
    const time = now.toTimeString().slice(0, 5);
    const [sh, sm] = rec.signIn.split(":").map(Number);
    const [eh, em] = time.split(":").map(Number);
    const hours = Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
    setDailyRecords(prev => ({
      ...prev,
      [selectedDate]: {
        ...prev[selectedDate],
        [workerId]: { ...prev[selectedDate]?.[workerId], signOut: time, hoursWorked: Math.round(hours * 10) / 10 }
      }
    }));
    showToast(`${workers.find(w => w.id === workerId)?.name} signed out at ${time}`);
  };

  const updateBags = (workerId) => {
    const val = parseInt(bagsInput[workerId] || 0);
    if (isNaN(val) || val < 0) return;
    setDailyRecords(prev => ({
      ...prev,
      [selectedDate]: {
        ...prev[selectedDate],
        [workerId]: { ...prev[selectedDate]?.[workerId], bags: val }
      }
    }));
    showToast(`Bags updated for ${workers.find(w => w.id === workerId)?.name}`);
  };

  const addWorker = () => {
    if (!newWorker.name.trim()) return;
    const id = Date.now();
    const worker = { ...newWorker, id };
    setWorkers(prev => [...prev, worker]);
    // Add to all existing date records
    setDailyRecords(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(date => {
        updated[date] = { ...updated[date], [id]: { signIn: null, signOut: null, bags: 0, hoursWorked: 0 } };
      });
      return updated;
    });
    setNewWorker({ name: "", role: "Harvester", phone: "", startDate: todayStr });
    setShowAddWorker(false);
    showToast(`${worker.name} added to the system`);
  };

  const deleteWorker = (id) => {
    setWorkers(prev => prev.filter(w => w.id !== id));
    setDailyRecords(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(date => {
        const copy = { ...updated[date] };
        delete copy[id];
        updated[date] = copy;
      });
      return updated;
    });
    setDeleteConfirm(null);
    showToast("Worker removed from system", "error");
  };

  // Monthly pay calculation
  const getMonthlyData = (workerId) => {
    let totalBags = 0, totalHours = 0, daysWorked = 0;
    Object.entries(dailyRecords).forEach(([date, records]) => {
      if (!date.startsWith(monthFilter)) return;
      const rec = records[workerId];
      if (!rec) return;
      if (rec.signIn) daysWorked++;
      totalBags += rec.bags || 0;
      totalHours += rec.hoursWorked || 0;
    });
    const bins = totalBags / BAGS_PER_BIN;
    const pay = bins * RATE_PER_BIN;
    return { totalBags, totalHours: Math.round(totalHours * 10) / 10, daysWorked, bins: Math.round(bins * 100) / 100, pay: Math.round(pay * 100) / 100 };
  };

  // Dashboard stats
  const todayRecord = dailyRecords[todayStr] || {};
  const presentToday = workers.filter(w => todayRecord[w.id]?.signIn).length;
  const totalBagsToday = workers.reduce((s, w) => s + (todayRecord[w.id]?.bags || 0), 0);
  const totalBinsToday = totalBagsToday / BAGS_PER_BIN;
  const progressPct = Math.min(100, Math.round((totalBinsToday / DAILY_TARGET_BINS) * 100));

  const workerChartData = workers.map(w => ({
    name: w.name.split(" ")[0],
    bags: todayRecord[w.id]?.bags || 0,
    bins: Math.round((todayRecord[w.id]?.bags || 0) / BAGS_PER_BIN * 10) / 10,
    hours: todayRecord[w.id]?.hoursWorked || 0,
  }));

  const pieData = [
    { name: "Present", value: presentToday },
    { name: "Absent", value: workers.length - presentToday },
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { ensureDateRecord(selectedDate); }, [selectedDate, workers.length]);

  return (
    <div style={{ fontFamily: "'Georgia', serif", background: "#0d1f0e", minHeight: "100vh", color: "#f0e6d0" }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #1a3a1c 0%, #0d2410 50%, #1e3d10 100%)", borderBottom: "2px solid #e8a020", padding: "0 16px" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 0", flexWrap: "wrap", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ fontSize: 40 }}>🍊</div>
            <div>
              <div style={{ fontSize: 22, fontWeight: "bold", color: "#e8a020", letterSpacing: 1 }}>CitrusTrack Pro</div>
              <div style={{ fontSize: 12, color: "#8fb87a", letterSpacing: 3, textTransform: "uppercase" }}>Harvesting Management System</div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 14, color: "#8fb87a" }}>{new Date().toLocaleDateString("en-ZA", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</div>
            <div style={{ fontSize: 12, color: "#e8a020" }}>Target: {DAILY_TARGET_BINS} bins/day • R{RATE_PER_BIN}/bin • {BAGS_PER_BIN} bags = 1 bin</div>
          </div>
        </div>
        {/* Nav */}
        <div style={{ display: "flex", gap: 4, paddingBottom: 0, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          {[
            { id: "dashboard", label: "📊 Dashboard" },
            { id: "attendance", label: "🕐 Attendance" },
            { id: "harvesting", label: "🧺 Harvesting" },
            { id: "workers", label: "👷 Workers" },
            { id: "payroll", label: "💰 Payroll" },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              background: tab === t.id ? "#e8a020" : "transparent",
              color: tab === t.id ? "#0d1f0e" : "#8fb87a",
              border: "none", borderRadius: "8px 8px 0 0", padding: "10px 20px",
              cursor: "pointer", fontWeight: tab === t.id ? "bold" : "normal",
              fontSize: 13, transition: "all 0.2s", whiteSpace: "nowrap", flexShrink: 0,
            }}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 9999,
          background: toast.type === "error" ? "#8b1a1a" : "#1a5c2a",
          border: `1px solid ${toast.type === "error" ? "#e05050" : "#4caf50"}`,
          color: "#fff", padding: "12px 20px", borderRadius: 8,
          boxShadow: "0 4px 20px rgba(0,0,0,0.4)", fontSize: 14,
          animation: "slideIn 0.3s ease"
        }}>{toast.msg}</div>
      )}

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "20px 16px" }}>

        {/* DASHBOARD */}
        {tab === "dashboard" && (
          <div>
            <h2 style={{ color: "#e8a020", marginBottom: 24, fontSize: 20 }}>📊 Today's Overview</h2>

            {/* KPI Cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16, marginBottom: 28 }}>
              {[
                { label: "Workers Present", value: `${presentToday}/${workers.length}`, icon: "👷", color: "#4caf50" },
                { label: "Total Bags Today", value: totalBagsToday.toLocaleString(), icon: "🧺", color: "#e8a020" },
                { label: "Bins Completed", value: `${Math.floor(totalBinsToday)}`, icon: "📦", color: "#2196f3" },
                { label: "Target Progress", value: `${progressPct}%`, icon: "🎯", color: progressPct >= 100 ? "#4caf50" : progressPct >= 70 ? "#e8a020" : "#e05050" },
                { label: "Today's Revenue", value: `R${Math.round(totalBinsToday * RATE_PER_BIN).toLocaleString()}`, icon: "💰", color: "#9c27b0" },
              ].map((kpi, i) => (
                <div key={i} style={{ background: "linear-gradient(145deg, #1a3a1c, #0f2610)", border: `1px solid ${kpi.color}40`, borderRadius: 12, padding: "20px", position: "relative", overflow: "hidden" }}>
                  <div style={{ fontSize: 28, marginBottom: 8 }}>{kpi.icon}</div>
                  <div style={{ fontSize: 28, fontWeight: "bold", color: kpi.color }}>{kpi.value}</div>
                  <div style={{ fontSize: 12, color: "#8fb87a", marginTop: 4 }}>{kpi.label}</div>
                  <div style={{ position: "absolute", right: -10, top: -10, fontSize: 60, opacity: 0.07 }}>{kpi.icon}</div>
                </div>
              ))}
            </div>

            {/* Target Progress Bar */}
            <div style={{ background: "#1a3a1c", border: "1px solid #2d5a2e", borderRadius: 12, padding: 20, marginBottom: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <span style={{ color: "#8fb87a", fontSize: 14 }}>Daily Target Progress</span>
                <span style={{ color: "#e8a020", fontSize: 14, fontWeight: "bold" }}>{Math.floor(totalBinsToday)} / {DAILY_TARGET_BINS} bins</span>
              </div>
              <div style={{ background: "#0d1f0e", borderRadius: 999, height: 24, overflow: "hidden", border: "1px solid #2d5a2e" }}>
                <div style={{
                  width: `${progressPct}%`, height: "100%",
                  background: progressPct >= 100 ? "linear-gradient(90deg, #4caf50, #81c784)" : progressPct >= 70 ? "linear-gradient(90deg, #e8a020, #ffcc80)" : "linear-gradient(90deg, #e05050, #ef9a9a)",
                  borderRadius: 999, transition: "width 0.5s ease",
                  display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 10,
                  fontSize: 12, fontWeight: "bold", color: "#0d1f0e"
                }}>{progressPct > 10 ? `${progressPct}%` : ""}</div>
              </div>
              {progressPct < 100 && <div style={{ color: "#8fb87a", fontSize: 12, marginTop: 8 }}>Still needed: {DAILY_TARGET_BINS - Math.floor(totalBinsToday)} bins ({(DAILY_TARGET_BINS - Math.floor(totalBinsToday)) * BAGS_PER_BIN} bags)</div>}
            </div>

            {/* Charts Row */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginBottom: 24 }}>
              <div style={{ background: "#1a3a1c", border: "1px solid #2d5a2e", borderRadius: 12, padding: 20 }}>
                <h3 style={{ color: "#e8a020", fontSize: 14, marginBottom: 16 }}>Bags per Worker Today</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={workerChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2d5a2e" />
                    <XAxis dataKey="name" stroke="#8fb87a" fontSize={11} />
                    <YAxis stroke="#8fb87a" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#0d1f0e", border: "1px solid #e8a020", color: "#f0e6d0", fontSize: 12 }} />
                    <Bar dataKey="bags" fill="#e8a020" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ background: "#1a3a1c", border: "1px solid #2d5a2e", borderRadius: 12, padding: 20 }}>
                <h3 style={{ color: "#e8a020", fontSize: 14, marginBottom: 16 }}>Attendance</h3>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false} fontSize={11}>
                      {pieData.map((_, i) => <Cell key={i} fill={["#e8a020", "#2d3a20"][i]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0d1f0e", border: "1px solid #e8a020", color: "#f0e6d0", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Hours chart */}
            <div style={{ background: "#1a3a1c", border: "1px solid #2d5a2e", borderRadius: 12, padding: 20 }}>
              <h3 style={{ color: "#e8a020", fontSize: 14, marginBottom: 16 }}>Hours Worked per Worker Today</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={workerChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2d5a2e" />
                  <XAxis dataKey="name" stroke="#8fb87a" fontSize={11} />
                  <YAxis stroke="#8fb87a" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#0d1f0e", border: "1px solid #e8a020", color: "#f0e6d0", fontSize: 12 }} />
                  <Bar dataKey="hours" fill="#4caf50" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* ATTENDANCE */}
        {tab === "attendance" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h2 style={{ color: "#e8a020", fontSize: 20 }}>🕐 Attendance Registry</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ color: "#8fb87a", fontSize: 13 }}>Date:</span>
                <input type="date" value={selectedDate} onChange={e => { setSelectedDate(e.target.value); ensureDateRecord(e.target.value); }}
                  style={{ background: "#1a3a1c", border: "1px solid #e8a020", borderRadius: 6, color: "#f0e6d0", padding: "6px 12px", fontSize: 13 }} />
              </div>
            </div>

            <div style={{ display: "grid", gap: 12 }}>
              {workers.map(w => {
                const rec = (dailyRecords[selectedDate] || {})[w.id] || {};
                const isSignedIn = rec.signIn && !rec.signOut;
                const isDone = rec.signIn && rec.signOut;
                return (
                  <div key={w.id} style={{
                    background: "linear-gradient(135deg, #1a3a1c, #0f2610)",
                    border: `1px solid ${isDone ? "#4caf5060" : isSignedIn ? "#e8a02060" : "#2d5a2e"}`,
                    borderRadius: 12, padding: "16px 20px",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: 12
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: "50%",
                        background: isDone ? "#1a4a2a" : isSignedIn ? "#3a3a10" : "#2a1a1a",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 20, border: `2px solid ${isDone ? "#4caf50" : isSignedIn ? "#e8a020" : "#555"}`
                      }}>
                        {isDone ? "✅" : isSignedIn ? "🟡" : "⭕"}
                      </div>
                      <div>
                        <div style={{ fontWeight: "bold", fontSize: 15 }}>{w.name}</div>
                        <div style={{ fontSize: 12, color: "#8fb87a" }}>{w.role}</div>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 11, color: "#8fb87a" }}>Sign In</div>
                        <div style={{ fontSize: 16, color: "#4caf50", fontWeight: "bold" }}>{rec.signIn || "—"}</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 11, color: "#8fb87a" }}>Sign Out</div>
                        <div style={{ fontSize: 16, color: "#e05050", fontWeight: "bold" }}>{rec.signOut || "—"}</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 11, color: "#8fb87a" }}>Hours</div>
                        <div style={{ fontSize: 16, color: "#e8a020", fontWeight: "bold" }}>{rec.hoursWorked || "0"}h</div>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8 }}>
                      {!rec.signIn && (
                        <button onClick={() => signIn(w.id)} style={{
                          background: "#1a5c2a", border: "1px solid #4caf50", color: "#4caf50",
                          borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: "bold"
                        }}>Sign In</button>
                      )}
                      {isSignedIn && (
                        <button onClick={() => signOut(w.id)} style={{
                          background: "#5c1a1a", border: "1px solid #e05050", color: "#e05050",
                          borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: "bold"
                        }}>Sign Out</button>
                      )}
                      {isDone && <span style={{ color: "#4caf50", fontSize: 13, padding: "8px 16px" }}>✓ Complete</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div style={{ marginTop: 24, background: "#1a3a1c", border: "1px solid #2d5a2e", borderRadius: 12, padding: 20 }}>
              <h3 style={{ color: "#e8a020", marginBottom: 12, fontSize: 15 }}>Day Summary — {selectedDate}</h3>
              <div style={{ display: "flex", gap: 32 }}>
                <div><span style={{ color: "#8fb87a" }}>Present: </span><span style={{ color: "#4caf50", fontWeight: "bold" }}>{workers.filter(w => (dailyRecords[selectedDate] || {})[w.id]?.signIn).length}</span></div>
                <div><span style={{ color: "#8fb87a" }}>Absent: </span><span style={{ color: "#e05050", fontWeight: "bold" }}>{workers.filter(w => !(dailyRecords[selectedDate] || {})[w.id]?.signIn).length}</span></div>
                <div><span style={{ color: "#8fb87a" }}>Avg Hours: </span><span style={{ color: "#e8a020", fontWeight: "bold" }}>{
                  (() => {
                    const worked = workers.filter(w => (dailyRecords[selectedDate] || {})[w.id]?.hoursWorked > 0);
                    if (!worked.length) return "0h";
                    return Math.round(worked.reduce((s, w) => s + ((dailyRecords[selectedDate] || {})[w.id]?.hoursWorked || 0), 0) / worked.length * 10) / 10 + "h";
                  })()
                }</span></div>
              </div>
            </div>
          </div>
        )}

        {/* HARVESTING */}
        {tab === "harvesting" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h2 style={{ color: "#e8a020", fontSize: 20 }}>🧺 Harvesting Tracker</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ color: "#8fb87a", fontSize: 13 }}>Date:</span>
                <input type="date" value={selectedDate} onChange={e => { setSelectedDate(e.target.value); ensureDateRecord(e.target.value); }}
                  style={{ background: "#1a3a1c", border: "1px solid #e8a020", borderRadius: 6, color: "#f0e6d0", padding: "6px 12px", fontSize: 13 }} />
              </div>
            </div>

            {/* Daily totals */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 16, marginBottom: 24 }}>
              {[
                { label: "Total Bags", value: workers.reduce((s, w) => s + ((dailyRecords[selectedDate] || {})[w.id]?.bags || 0), 0).toLocaleString(), color: "#e8a020", icon: "🧺" },
                { label: "Total Bins", value: Math.floor(workers.reduce((s, w) => s + ((dailyRecords[selectedDate] || {})[w.id]?.bags || 0), 0) / BAGS_PER_BIN), color: "#4caf50", icon: "📦" },
                { label: "Revenue", value: "R" + Math.round(workers.reduce((s, w) => s + ((dailyRecords[selectedDate] || {})[w.id]?.bags || 0), 0) / BAGS_PER_BIN * RATE_PER_BIN).toLocaleString(), color: "#9c27b0", icon: "💰" },
                { label: "Target Left", value: Math.max(0, DAILY_TARGET_BINS - Math.floor(workers.reduce((s, w) => s + ((dailyRecords[selectedDate] || {})[w.id]?.bags || 0), 0) / BAGS_PER_BIN)) + " bins", color: "#e05050", icon: "🎯" },
              ].map((k, i) => (
                <div key={i} style={{ background: "#1a3a1c", border: `1px solid ${k.color}40`, borderRadius: 10, padding: 16 }}>
                  <div style={{ fontSize: 24 }}>{k.icon}</div>
                  <div style={{ fontSize: 24, fontWeight: "bold", color: k.color, marginTop: 4 }}>{k.value}</div>
                  <div style={{ fontSize: 12, color: "#8fb87a" }}>{k.label}</div>
                </div>
              ))}
            </div>

            {/* Worker rows */}
            <div style={{ display: "grid", gap: 10 }}>
              {workers.map(w => {
                const rec = (dailyRecords[selectedDate] || {})[w.id] || {};
                const bins = rec.bags ? rec.bags / BAGS_PER_BIN : 0;
                const earnings = bins * RATE_PER_BIN;
                return (
                  <div key={w.id} style={{ background: "#1a3a1c", border: "1px solid #2d5a2e", borderRadius: 12, padding: "16px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
                      <div style={{ minWidth: 180 }}>
                        <div style={{ fontWeight: "bold" }}>{w.name}</div>
                        <div style={{ fontSize: 12, color: "#8fb87a" }}>{w.role}</div>
                      </div>

                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          type="number" min="0"
                          placeholder={(rec.bags || 0).toString()}
                          value={bagsInput[w.id] ?? ""}
                          onChange={e => setBagsInput(prev => ({ ...prev, [w.id]: e.target.value }))}
                          style={{ width: 80, background: "#0d1f0e", border: "1px solid #e8a020", borderRadius: 6, color: "#f0e6d0", padding: "6px 10px", fontSize: 13, textAlign: "center" }}
                        />
                        <span style={{ color: "#8fb87a", fontSize: 13 }}>bags</span>
                        <button onClick={() => updateBags(w.id)} style={{
                          background: "#1a5c2a", border: "1px solid #4caf50", color: "#4caf50",
                          borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 12
                        }}>Update</button>
                      </div>

                      <div style={{ display: "flex", gap: 28, alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 11, color: "#8fb87a" }}>Bags</div>
                          <div style={{ fontSize: 18, color: "#e8a020", fontWeight: "bold" }}>{rec.bags || 0}</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 11, color: "#8fb87a" }}>Bins</div>
                          <div style={{ fontSize: 18, color: "#4caf50", fontWeight: "bold" }}>{Math.round(bins * 100) / 100}</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 11, color: "#8fb87a" }}>Earned</div>
                          <div style={{ fontSize: 18, color: "#9c27b0", fontWeight: "bold" }}>R{Math.round(earnings)}</div>
                        </div>
                      </div>

                      {/* Mini bar */}
                      <div style={{ width: 120 }}>
                        <div style={{ fontSize: 10, color: "#8fb87a", marginBottom: 4 }}>vs avg target ({Math.round(DAILY_TARGET_BINS / workers.length)} bins)</div>
                        <div style={{ background: "#0d1f0e", borderRadius: 999, height: 8, overflow: "hidden" }}>
                          <div style={{
                            width: `${Math.min(100, (bins / (DAILY_TARGET_BINS / workers.length)) * 100)}%`,
                            height: "100%", background: "#e8a020", borderRadius: 999
                          }} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* WORKERS */}
        {tab === "workers" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h2 style={{ color: "#e8a020", fontSize: 20 }}>👷 Worker Management</h2>
              <button onClick={() => setShowAddWorker(true)} style={{
                background: "linear-gradient(135deg, #e8a020, #c07810)", color: "#0d1f0e",
                border: "none", borderRadius: 8, padding: "10px 20px", cursor: "pointer",
                fontWeight: "bold", fontSize: 14
              }}>+ Add Worker</button>
            </div>

            {/* Add Worker Modal */}
            {showAddWorker && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ background: "#1a3a1c", border: "1px solid #e8a020", borderRadius: 16, padding: 32, width: 400, boxShadow: "0 8px 40px rgba(0,0,0,0.6)" }}>
                  <h3 style={{ color: "#e8a020", marginBottom: 24 }}>Add New Worker</h3>
                  {[
                    { label: "Full Name*", key: "name", type: "text", placeholder: "e.g. John Smith" },
                    { label: "Phone", key: "phone", type: "tel", placeholder: "e.g. 071 234 5678" },
                    { label: "Start Date", key: "startDate", type: "date", placeholder: "" },
                  ].map(f => (
                    <div key={f.key} style={{ marginBottom: 16 }}>
                      <label style={{ display: "block", color: "#8fb87a", fontSize: 12, marginBottom: 6 }}>{f.label}</label>
                      <input type={f.type} value={newWorker[f.key]} placeholder={f.placeholder}
                        onChange={e => setNewWorker(prev => ({ ...prev, [f.key]: e.target.value }))}
                        style={{ width: "100%", background: "#0d1f0e", border: "1px solid #2d5a2e", borderRadius: 8, color: "#f0e6d0", padding: "10px 14px", fontSize: 14, boxSizing: "border-box" }} />
                    </div>
                  ))}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: "block", color: "#8fb87a", fontSize: 12, marginBottom: 6 }}>Role</label>
                    <select value={newWorker.role} onChange={e => setNewWorker(prev => ({ ...prev, role: e.target.value }))}
                      style={{ width: "100%", background: "#0d1f0e", border: "1px solid #2d5a2e", borderRadius: 8, color: "#f0e6d0", padding: "10px 14px", fontSize: 14 }}>
                      <option>Harvester</option>
                      <option>Senior Harvester</option>
                      <option>Team Leader</option>
                      <option>Supervisor</option>
                    </select>
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <button onClick={addWorker} style={{ flex: 1, background: "linear-gradient(135deg, #e8a020, #c07810)", color: "#0d1f0e", border: "none", borderRadius: 8, padding: "12px", cursor: "pointer", fontWeight: "bold", fontSize: 14 }}>Add Worker</button>
                    <button onClick={() => setShowAddWorker(false)} style={{ flex: 1, background: "transparent", color: "#8fb87a", border: "1px solid #2d5a2e", borderRadius: 8, padding: "12px", cursor: "pointer", fontSize: 14 }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ background: "#2a1010", border: "1px solid #e05050", borderRadius: 16, padding: 32, width: 360, textAlign: "center" }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
                  <h3 style={{ color: "#e05050", marginBottom: 12 }}>Remove Worker?</h3>
                  <p style={{ color: "#8fb87a", marginBottom: 24, fontSize: 14 }}>This will permanently remove <strong style={{ color: "#f0e6d0" }}>{workers.find(w => w.id === deleteConfirm)?.name}</strong> and all their records.</p>
                  <div style={{ display: "flex", gap: 12 }}>
                    <button onClick={() => deleteWorker(deleteConfirm)} style={{ flex: 1, background: "#8b1a1a", color: "#e05050", border: "1px solid #e05050", borderRadius: 8, padding: "12px", cursor: "pointer", fontWeight: "bold" }}>Remove</button>
                    <button onClick={() => setDeleteConfirm(null)} style={{ flex: 1, background: "transparent", color: "#8fb87a", border: "1px solid #2d5a2e", borderRadius: 8, padding: "12px", cursor: "pointer" }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: "grid", gap: 12 }}>
              {workers.map(w => (
                <div key={w.id} style={{ background: "linear-gradient(135deg, #1a3a1c, #0f2610)", border: "1px solid #2d5a2e", borderRadius: 12, padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                    <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#0d1f0e", border: "2px solid #e8a020", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>
                      {w.role === "Team Leader" ? "👑" : w.role === "Supervisor" ? "🎖️" : w.role === "Senior Harvester" ? "⭐" : "👷"}
                    </div>
                    <div>
                      <div style={{ fontWeight: "bold", fontSize: 16 }}>{w.name}</div>
                      <div style={{ fontSize: 12, color: "#e8a020", marginTop: 2 }}>{w.role}</div>
                      <div style={{ fontSize: 12, color: "#8fb87a", marginTop: 2 }}>📱 {w.phone || "Not provided"} • Joined: {w.startDate}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => setDeleteConfirm(w.id)} style={{
                      background: "#3a1010", border: "1px solid #e05050", color: "#e05050",
                      borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13
                    }}>🗑️ Remove</button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, color: "#8fb87a", fontSize: 13 }}>Total workers: {workers.length}</div>
          </div>
        )}

        {/* PAYROLL */}
        {tab === "payroll" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h2 style={{ color: "#e8a020", fontSize: 20 }}>💰 Monthly Payroll</h2>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ color: "#8fb87a", fontSize: 13 }}>Month:</span>
                <input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
                  style={{ background: "#1a3a1c", border: "1px solid #e8a020", borderRadius: 6, color: "#f0e6d0", padding: "6px 12px", fontSize: 13 }} />
              </div>
            </div>

            {/* Monthly totals */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16, marginBottom: 24 }}>
              {(() => {
                const totals = workers.reduce((acc, w) => {
                  const d = getMonthlyData(w.id);
                  acc.bags += d.totalBags;
                  acc.bins += d.bins;
                  acc.pay += d.pay;
                  return acc;
                }, { bags: 0, bins: 0, pay: 0 });
                return [
                  { label: "Total Bags This Month", value: Math.round(totals.bags).toLocaleString(), color: "#e8a020", icon: "🧺" },
                  { label: "Total Bins This Month", value: Math.round(totals.bins).toLocaleString(), color: "#4caf50", icon: "📦" },
                  { label: "Total Payroll", value: "R" + Math.round(totals.pay).toLocaleString(), color: "#9c27b0", icon: "💰" },
                ].map((k, i) => (
                  <div key={i} style={{ background: "#1a3a1c", border: `1px solid ${k.color}40`, borderRadius: 10, padding: 20 }}>
                    <div style={{ fontSize: 28 }}>{k.icon}</div>
                    <div style={{ fontSize: 28, fontWeight: "bold", color: k.color, marginTop: 4 }}>{k.value}</div>
                    <div style={{ fontSize: 12, color: "#8fb87a" }}>{k.label}</div>
                  </div>
                ));
              })()}
            </div>

            {/* Worker pay table */}
            <div style={{ background: "#1a3a1c", border: "1px solid #2d5a2e", borderRadius: 12, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#0f2610" }}>
                    {["Worker", "Role", "Days Worked", "Total Hours", "Total Bags", "Total Bins", "Monthly Pay"].map(h => (
                      <th key={h} style={{ padding: "14px 16px", textAlign: "left", color: "#e8a020", fontSize: 12, letterSpacing: 1, textTransform: "uppercase", borderBottom: "1px solid #2d5a2e" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {workers.map((w, i) => {
                    const d = getMonthlyData(w.id);
                    return (
                      <tr key={w.id} style={{ borderBottom: "1px solid #1a3a1c20", background: i % 2 === 0 ? "transparent" : "#0f261010" }}>
                        <td style={{ padding: "14px 16px", fontWeight: "bold" }}>{w.name}</td>
                        <td style={{ padding: "14px 16px", color: "#e8a020", fontSize: 13 }}>{w.role}</td>
                        <td style={{ padding: "14px 16px", color: "#8fb87a" }}>{d.daysWorked} days</td>
                        <td style={{ padding: "14px 16px", color: "#8fb87a" }}>{d.totalHours}h</td>
                        <td style={{ padding: "14px 16px", color: "#f0e6d0" }}>{d.totalBags.toLocaleString()}</td>
                        <td style={{ padding: "14px 16px", color: "#4caf50" }}>{d.bins}</td>
                        <td style={{ padding: "14px 16px", fontWeight: "bold", fontSize: 16, color: "#9c27b0" }}>R{Math.round(d.pay).toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pay chart */}
            <div style={{ marginTop: 24, background: "#1a3a1c", border: "1px solid #2d5a2e", borderRadius: 12, padding: 20 }}>
              <h3 style={{ color: "#e8a020", fontSize: 14, marginBottom: 16 }}>Monthly Pay Comparison</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={workers.map(w => ({ name: w.name.split(" ")[0], pay: Math.round(getMonthlyData(w.id).pay), bags: getMonthlyData(w.id).totalBags }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2d5a2e" />
                  <XAxis dataKey="name" stroke="#8fb87a" fontSize={11} />
                  <YAxis stroke="#8fb87a" fontSize={11} tickFormatter={v => `R${v}`} />
                  <Tooltip contentStyle={{ background: "#0d1f0e", border: "1px solid #e8a020", color: "#f0e6d0", fontSize: 12 }} formatter={v => `R${v}`} />
                  <Bar dataKey="pay" fill="#9c27b0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Rate info */}
            <div style={{ marginTop: 16, background: "#1a3a1c", border: "1px solid #2d5a2e", borderRadius: 10, padding: 16, fontSize: 13, color: "#8fb87a" }}>
              <strong style={{ color: "#e8a020" }}>Pay Formula: </strong>
              Total Bags ÷ {BAGS_PER_BIN} bags/bin = Bins × R{RATE_PER_BIN}/bin = Monthly Pay
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideIn { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        * { box-sizing: border-box; }
        input:focus, select:focus { outline: 2px solid #e8a020; outline-offset: 2px; }
        button:hover { opacity: 0.88; transform: translateY(-1px); transition: all 0.15s; }
        ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: #0d1f0e; } ::-webkit-scrollbar-thumb { background: #2d5a2e; border-radius: 3px; }
      `}</style>
    </div>
  );
}
